/** PostHog production monitoring tier for `game_thumbnail_diagnostic` (PLA-469). */

export const ICON_DIAGNOSTIC_INCIDENT_FLAG = "icon-diagnostics-full";
export const ICON_DIAGNOSTIC_SESSION_CAP = 20;
export const ICON_DIAGNOSTIC_ATTEMPT0_SAMPLE_PERCENT = 10;

export type IconDiagnosticReason =
  | "image_error"
  | "image_error_final"
  | "image_hover_retry"
  | "image_recovered"
  | "visible_image_still_not_loaded"
  | "video_overlay_not_ready";

export type IconDiagnosticTier = "prod" | "full";

type PostHogDiagnosticClient = {
  // posthog-js returns boolean | undefined; keep param list minimal so the real
  // (key, options?) signature remains assignable under strictFunctionTypes.
  isFeatureEnabled?: (key: string) => boolean | undefined;
  get_session_id?: () => string;
};

export type ShouldCaptureIconDiagnosticInput = {
  reason: string;
  imageUrl: string;
  attempt?: number;
  posthog?: PostHogDiagnosticClient | null;
  sessionId?: string | null;
};

const sessionCaptureCounts = new Map<string, number>();

export function isIconDiagnosticProductionMonitoringTier(): boolean {
  // Requires APP_ENV to be inlined for client bundles via next.config.mjs `env`.
  return process.env.APP_ENV === "production";
}

export function isIconDiagnosticIncidentMode(posthog?: PostHogDiagnosticClient | null): boolean {
  if (process.env.NEXT_PUBLIC_ICON_DIAG_FULL === "1") return true;
  return posthog?.isFeatureEnabled?.(ICON_DIAGNOSTIC_INCIDENT_FLAG) === true;
}

/** Skip scheduling stall/video diagnostic timers in prod unless incident mode is on. */
export function shouldSkipIconDiagnosticProbes(posthog?: PostHogDiagnosticClient | null): boolean {
  return isIconDiagnosticProductionMonitoringTier() && !isIconDiagnosticIncidentMode(posthog);
}

export function iconDiagnosticSampleBucket(sessionId: string, imageUrl: string): number {
  return hashString(`${sessionId}:${imageUrl}`) % 100;
}

export function shouldSampleIconErrorAttempt0(sessionId: string, imageUrl: string): boolean {
  return iconDiagnosticSampleBucket(sessionId, imageUrl) < ICON_DIAGNOSTIC_ATTEMPT0_SAMPLE_PERCENT;
}

export function resolveIconDiagnosticSessionId(
  posthog?: PostHogDiagnosticClient | null,
  explicitSessionId?: string | null
): string {
  if (explicitSessionId) return explicitSessionId;
  return posthog?.get_session_id?.() ?? "unknown-session";
}

export function getIconDiagnosticSessionCaptureCount(sessionId: string): number {
  return sessionCaptureCounts.get(sessionId) ?? 0;
}

export function recordIconDiagnosticCapture(sessionId: string): void {
  sessionCaptureCounts.set(sessionId, getIconDiagnosticSessionCaptureCount(sessionId) + 1);
}

export function resetIconDiagnosticCaptureStateForTests(): void {
  sessionCaptureCounts.clear();
}

export function resolveIconDiagnosticTier(
  posthog?: PostHogDiagnosticClient | null
): IconDiagnosticTier {
  if (!isIconDiagnosticProductionMonitoringTier()) return "full";
  if (isIconDiagnosticIncidentMode(posthog)) return "full";
  return "prod";
}

export function shouldCaptureIconDiagnostic({
  reason,
  imageUrl,
  attempt,
  posthog,
  sessionId: explicitSessionId,
}: ShouldCaptureIconDiagnosticInput): boolean {
  if (!isIconDiagnosticProductionMonitoringTier()) return true;
  if (isIconDiagnosticIncidentMode(posthog)) return true;

  if (reason === "image_error_final") return true;

  const sessionId = resolveIconDiagnosticSessionId(posthog, explicitSessionId);
  if (getIconDiagnosticSessionCaptureCount(sessionId) >= ICON_DIAGNOSTIC_SESSION_CAP) {
    return false;
  }

  if (reason === "image_hover_retry") return true;

  if (reason === "image_error" && attempt === 0) {
    return shouldSampleIconErrorAttempt0(sessionId, imageUrl);
  }

  return false;
}

function hashString(input: string): number {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = (hash * 31 + input.charCodeAt(i)) >>> 0;
  }
  return hash;
}
